import { AxiosInstance, AxiosResponse } from "axios";
import { ApiResponse } from "../public-types";
import {
  ConsentStatusCode,
  ContractAccessLogPageTokenParams,
  ContractAccessLogPageTokenResponse,
  EmailExistsInfo,
  EmailVerificationResponseData,
  MemberConsentAgreementCreateResponse,
  MemberConsentAgreementsRequestParam,
  MemberConsentAgreementsResponse,
  MemberData,
  MemberUpdateResponseData,
  SmsCodeVerificationResponseData,
  SmsVerificationResponseData,
} from "./member-types";

/**
 * TPB01. 회원 가입
 */
export async function patchJoinAsync(
  axios: AxiosInstance,
  params?: {
    email?: string; // 가입 이메일
    marketingConsent?: boolean; // 마케팅 동의 여부
  },
): Promise<AxiosResponse<ApiResponse<MemberUpdateResponseData>>> {
  return await axios.patch("/api/court/mb/userinfo/join", {
    email: params?.email,
    marketingConsent: params?.marketingConsent,
  });
}

/**
 * 이메일 중복 확인
 */
export async function getEmailExistsAsync(
  axios: AxiosInstance,
  params?: { email: string },
): Promise<AxiosResponse<ApiResponse<EmailExistsInfo>>> {
  const encodedEmail = encodeURIComponent(params?.email ?? "");
  return await axios.get<ApiResponse<EmailExistsInfo>>(`/api/court/mb/emails/${encodedEmail}/exists`);
}

/* 
  TPB04. 내 프로필 조회
*/
export async function getMemberMeAsync(axios: AxiosInstance, params?: any): Promise<AxiosResponse<ApiResponse<MemberData>>> {
  return await axios.get<ApiResponse<MemberData>>("/api/court/mb/me");
}

/**
 * TPB05. 마케팅 수신 동의 수정 처리
 */
export async function patchMarketingConsentAsync(
  axios: AxiosInstance,
  params?: {
    marketingConsent: boolean; // 마케팅 수신 동의 여부
  },
): Promise<AxiosResponse<ApiResponse<MemberUpdateResponseData>>> {
  return await axios.patch("/api/court/mb/me/marketing-consent", {
    marketingConsent: !!params?.marketingConsent,
  });
}

/**
 * TPB06. SMS 인증 코드 발송
 */
export async function postSmsVerificationsAsync(
  axios: AxiosInstance,
  params?: {
    phoneNumber: string; // 점유 인증 대상 휴대폰 번호 (010-1234-1234)
    state?: string; // SMS 인증 처리 문맥 상태 식별 정보
  },
): Promise<AxiosResponse<ApiResponse<SmsVerificationResponseData>>> {
  if (!params?.phoneNumber) {
    throw Error("phoneNumber is not found.");
  }
  return await axios.post("/api/court/mb/sms/verifications", {
    phoneNumber: params.phoneNumber,
    state: params?.state,
  });
}

/**
 * TPB07. 휴대폰 번호 변경 처리
 */
export async function patchMemberPhoneNumberAsync(
  axios: AxiosInstance,
  params?: {
    state: string; // SMS 인증 처리 문맥 상태 식별 정보
    phoneNumber: string; // 점유 인증 대상 휴대폰 번호
    smsCode: string; // SMS 인증 코드
  },
): Promise<AxiosResponse<ApiResponse<MemberUpdateResponseData>>> {
  if (!params?.state) {
    throw Error("state is not found.");
  }
  if (!params?.phoneNumber) {
    throw Error("phoneNumber is not found.");
  }
  if (!params?.smsCode) {
    throw Error("smsCode is not found.");
  }
  return await axios.patch("/api/court/mb/me/phone-number", {
    state: params.state,
    phoneNumber: params.phoneNumber,
    smsCode: params?.smsCode,
  });
}

/**
 * TPB08. 이메일 인증 링크 발송
 */
export async function postEmailVerificationsAsync(
  axios: AxiosInstance,
  params?: {
    email: string; // 인증 대상 이메일 주소
  },
): Promise<AxiosResponse<ApiResponse<EmailVerificationResponseData>>> {
  if (!params?.email) {
    throw Error("email is not found.");
  }
  return await axios.post("/api/court/mb/email/verifications", {
    email: params.email,
    type: "COURT", // court 에서 이메일 발송
  });
}

/**
 * TPB09. SMS 인증 코드 검증 조회
 */
export async function getSmsVerificationsAsync(
  axios: AxiosInstance,
  params?: {
    state: string; // SMS 인증 처리 문맥 상태 식별 정보
    phoneNumber: string; // 인증 대상 휴대폰 번호
    smsCode: string; // SMS 인증 코드
  },
): Promise<AxiosResponse<ApiResponse<SmsCodeVerificationResponseData>>> {
  if (!params?.state) {
    throw Error("state is not found.");
  }
  if (!params?.phoneNumber) {
    throw Error("phoneNumber is not found.");
  }
  if (!params?.smsCode) {
    throw Error("smsCode is not found.");
  }
  return await axios.get(`/api/court/mb/sms/verifications/${params.state}`, {
    params: {
      phoneNumber: params.phoneNumber,
      smsCode: params.smsCode,
    },
  });
}

// TAC22. 계약에 대한 출입로그 조회 (page token)
export async function getContractAccessLogsByPageTokenAsync(
  axios: AxiosInstance,
  params?: ContractAccessLogPageTokenParams,
): Promise<AxiosResponse<ContractAccessLogPageTokenResponse>> {
  const { contractManageId, ...queryParams } = params ?? ({} as ContractAccessLogPageTokenParams);
  return await axios.get<ContractAccessLogPageTokenResponse>(`/api/court/ac2/access/v1/contract-manage/${contractManageId}/access-logs`, {
    params: queryParams,
  });
}

/**
 * TMBV201. 탈퇴 요청 처리
 */
export async function postWithdrawalAsync(axios: AxiosInstance): Promise<AxiosResponse<ApiResponse<any>>> {
  return await axios.post("/api/court/mb/me/withdrawal");
}

/**
 * TMCV201. 나의 동의 목록 조회
 */
export async function getMyConsentAgreementsAsync(
  axios: AxiosInstance,
  params?: MemberConsentAgreementsRequestParam,
): Promise<AxiosResponse<MemberConsentAgreementsResponse>> {
  return await axios.get<MemberConsentAgreementsResponse>("/api/court/mb/my/consent-agreements", {
    params,
  });
}

/**
 * TMCV202. 나의 동의 레코드 생성 처리
 */
export async function createMyConsentRecordAsync(
  axios: AxiosInstance,
  params?: {
    agreementCode: string; // 동의 대상 코드
    revision: number; // 동의 리비전 번호
    statusCode: ConsentStatusCode; // 동의 여부
  },
): Promise<AxiosResponse<MemberConsentAgreementCreateResponse>> {
  if (!params?.agreementCode) {
    throw Error("agreementCode is not found.");
  }
  if (typeof params?.revision !== "number") {
    throw Error("revision is not found.");
  }
  return await axios.post<MemberConsentAgreementCreateResponse>(
    `/api/court/mb/my/consent-agreements/${params.agreementCode}/revisions/${params.revision}/records`,
    {
      statusCode: params.statusCode,
    },
  );
}
